import {
  adherenceFor,
  consistency,
  describeConsistency,
  describeStreak,
  describeValue,
  dueness,
  isMarkerKind,
  resolveRange,
  type CheckInTemplate,
  type DueCheckIn,
  type Entry,
  type Timestamp,
  type Track,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { BackupPrompt } from '../../backup/BackupPrompt.js'
import { Button } from '../../components/Button.js'
import { ConsistencyStrip } from '../../components/ConsistencyStrip.js'
import { EmptyState } from '../../components/EmptyState.js'
import { ExamplePackOffer } from '../../components/ExamplePackOffer.js'
import { trackColour } from '../../charts/colours.js'
import {
  formatRelativeDay,
  formatTime,
  formatWeekdayDate,
  greetingFor,
} from '../../format/dates.js'
import { useServices } from '../../services/ServicesProvider.js'
import styles from './Home.module.css'

const RECENT_COUNT = 4
const STRIP_DAYS = 14

interface Loaded {
  readonly templates: readonly CheckInTemplate[]
  readonly tracks: ReadonlyMap<string, Track>
  readonly recent: readonly Entry[]
  readonly due: readonly DueCheckIn[]
  readonly window: readonly Entry[]
  readonly streak: number
  readonly hasEntries: boolean
}

/**
 * Where logging starts (T-304, refitted in T-901).
 *
 * A launcher for the thing the app is for, arranged around **one** check-in:
 * the one most likely to be wanted right now, in the accent, with everything
 * else deliberately quieter so as not to compete with it. Due-ness comes from
 * reminders (T-902), so the promotion is a fact about the user's own schedule
 * rather than a guess.
 *
 * Nothing on this screen knows what any track measures. The cards read their
 * names, the chips read `describeValue`, and a database about sleep looks
 * exactly like one about anything else.
 */
export function HomeScreen() {
  const { repos, clock } = useServices()
  const navigate = useNavigate()

  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [now, setNow] = useState<Timestamp>(() => clock.now())

  const reload = useCallback(async () => {
    const at = clock.now()
    const zone = clock.timeZone()

    const [templates, allTracks, recent, schedules, bounds] = await Promise.all([
      repos.templates.list(),
      repos.tracks.list({ includeArchived: true }),
      repos.entries.recent(RECENT_COUNT),
      repos.reminders.list(),
      repos.entries.bounds(),
    ])

    /*
     * Two windows, because they answer different questions.
     *
     * The strip is a fixed fortnight — "lately" is a fixed length or the shape
     * is not comparable week to week. The streak is `allTime`, so a run of
     * sixty days reads as sixty rather than being clipped by whatever window
     * the strip happens to use.
     */
    const streakRange = resolveRange(
      { kind: 'preset', preset: 'allTime' },
      {
        clock,
        bounds: {
          ...(bounds.first === undefined ? {} : { first: bounds.first.at }),
          ...(bounds.last === undefined ? {} : { last: bounds.last.at }),
        },
      },
    )
    const stripRange = resolveRange({ kind: 'preset', preset: 'last30' }, { clock })

    const [forStreak, forStrip] = await Promise.all([
      repos.entries.listByRange(streakRange),
      repos.entries.listByRange(stripRange),
    ])

    setNow(at)
    setLoaded({
      templates,
      tracks: new Map(allTracks.map((track) => [track.id, track])),
      recent,
      due: dueness(templates, schedules, forStrip, at, zone),
      window: forStrip,
      streak: adherenceFor(forStreak, streakRange).trailingStreakDays,
      hasEntries: bounds.first !== undefined,
    })
  }, [clock, repos])

  useEffect(() => {
    void reload()
  }, [reload])

  if (loaded === null) {
    return (
      <>
        <h1 className="visually-hidden">Home</h1>
        <p>Loading…</p>
      </>
    )
  }

  const { templates, tracks, recent, due, window, streak, hasEntries } = loaded
  const hasTracks = tracks.size > 0
  const streakLabel = describeStreak(streak)
  const strip = consistency(window, STRIP_DAYS, now, clock.timeZone())

  return (
    <>
      <header className={styles['header']}>
        <div className={styles['heading']}>
          {/*
            The screen names itself, as every other one does, and does it out of
            sight because the navigation already says where you are. The
            greeting below is the biggest thing on the page but it is a
            salutation, not a section title: a heading that reads "Good
            afternoon" tells someone navigating by headings nothing about which
            screen they landed on, and says something different by evening.
          */}
          <h1 className="visually-hidden">Home</h1>
          <p className={styles['eyebrow']}>{formatWeekdayDate(now)}</p>
          <p className={styles['greeting']}>{greetingFor(now)}</p>
        </div>
        {/*
          Hidden below two days: a "streak" of one is just today, and calling
          it a streak is the kind of flattery that makes the next one worth
          less. Nothing here ever says a streak is at risk (T-903).
        */}
        {streak >= 2 && streakLabel !== null && (
          <span className={styles['streak']}>{streakLabel}</span>
        )}
      </header>

      {/*
        Above the check-in cards, and deliberately allowed to out-shout the due
        one. Both want to be the loudest thing here; T-503's escalation wins,
        because the cost of losing everything is total and the cost of logging
        an hour late is nothing.
      */}
      <BackupPrompt />

      {hasTracks ? (
        <div className={styles['checkins']}>
          {due.map((item) => (
            <CheckInCard
              key={item.template.id}
              item={item}
              tracks={tracks}
              now={now}
              onOpen={() => void navigate(`/log/${item.template.id}`)}
            />
          ))}

          {/*
            The escape hatch, always available. Not everything worth recording
            fits a check-in someone thought of in advance.
          */}
          <button
            type="button"
            className={styles['adhoc']}
            onClick={() => void navigate('/log')}
          >
            <Plus />
            Log anything
          </button>
        </div>
      ) : (
        <EmptyState
          title="Nothing set up yet"
          body="Start by deciding what you want to measure. A track can be a 1–10 scale, a yes/no, or a set of options — anything you want to record over time."
          action={
            <div className={styles['start']}>
              <Button variant="primary" onClick={() => void navigate('/tracks/new')}>
                Set up a track
              </Button>
              {/*
                Renders nothing once the packs have been offered, so this stops
                being a first-run screen the moment it should.
              */}
              <ExamplePackOffer onInstalled={reload} />
            </div>
          }
        />
      )}

      {hasEntries && (
        <div className={styles['stripRow']}>
          <ConsistencyStrip days={strip.days} caption={describeConsistency(strip)} />
        </div>
      )}

      {recent.length > 0 && (
        <section className={styles['recentSection']}>
          <div className={styles['recentHeader']}>
            <h2 className={styles['recentTitle']}>Recently recorded</h2>
            <button
              type="button"
              className={styles['allHistory']}
              onClick={() => void navigate('/timeline')}
            >
              All history
            </button>
          </div>
          <div className={styles['recent']}>
            {recent.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={styles['entry']}
                onClick={() => void navigate(`/timeline/${entry.id}`)}
              >
                <span className={styles['entryHeader']}>
                  <span className={styles['entryTemplate']}>
                    {templates.find((t) => t.id === entry.templateId)?.name ?? 'Logged'}
                  </span>
                  <span className={styles['entryWhen']}>
                    {/* The entry's own zone, never the reader's — an entry
                        recorded abroad keeps the day and time it was made. */}
                    {formatRelativeDay(entry.at, now)} {formatTime(entry.at)}
                  </span>
                </span>
                <span className={styles['chips']}>
                  {entry.measurements.map((measurement) => {
                    const track = tracks.get(measurement.trackId)
                    if (!track) {
                      return null
                    }
                    return (
                      <span key={measurement.trackId} className={styles['chip']}>
                        <span
                          className={styles['chipDot']}
                          style={{ background: trackColour(track.id) }}
                        />
                        {track.name}
                        {/* A marker's name is what happened, so it carries no
                            value beside it. */}
                        {!isMarkerKind(track.kind) && (
                          <>
                            {' '}
                            <span className={styles['chipValue']}>
                              {/* Through the track's own configuration: an enum
                                  reads as its option label and a boolean as its
                                  author's word, never as an id or `true`. */}
                              {describeValue(track, measurement.value) ?? '—'}
                            </span>
                          </>
                        )}
                      </span>
                    )
                  })}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/*
        The way back to setup, from the screen you land on.

        Load-bearing, not decoration: for a while the only links to these
        screens were inside empty states, which disappear as soon as they are no
        longer empty — so making a second check-in meant typing a URL.
        `shell.spec.ts` asserts each one by tapping it.
      */}
      <nav className={styles['manage']} aria-label="Set up">
        <button type="button" onClick={() => void navigate('/tracks')}>
          Tracks
        </button>
        <button type="button" onClick={() => void navigate('/templates')}>
          Check-ins
        </button>
        <button type="button" onClick={() => void navigate('/tags')}>
          Tags
        </button>
      </nav>
    </>
  )
}

/**
 * One check-in, loud or quiet.
 *
 * The same button either way, so the promoted one is a change of emphasis
 * rather than a different control that behaves differently.
 */
function CheckInCard({
  item,
  tracks,
  now,
  onOpen,
}: {
  readonly item: DueCheckIn
  readonly tracks: ReadonlyMap<string, Track>
  readonly now: Timestamp
  readonly onOpen: () => void
}) {
  const names = item.template.trackIds
    .map((id) => tracks.get(id)?.name)
    .filter((name): name is string => name !== undefined)
    .join(' · ')

  if (item.due) {
    return (
      <button type="button" className={styles['due']} onClick={onOpen}>
        <span className={styles['dueText']}>
          <span className={styles['dueLabel']}>Due now</span>
          <span className={styles['dueName']}>{item.template.name}</span>
          <span className={styles['dueTracks']}>{names}</span>
        </span>
        <span className={styles['dueArrow']} aria-hidden="true">
          <Arrow />
        </span>
      </button>
    )
  }

  return (
    <button type="button" className={styles['quiet']} onClick={onOpen}>
      <span className={styles['quietText']}>
        <span className={styles['quietName']}>{item.template.name}</span>
        <span className={styles['quietTracks']}>{names}</span>
      </span>
      {/*
        Only when a reminder says so. A template with no schedule shows no
        time rather than an invented one.
      */}
      {item.at !== null && (
        <span className={styles['quietWhen']}>{whenLabel(item.at, now)}</span>
      )}
    </button>
  )
}

/** "08:00", or the day too once it is not today's. */
function whenLabel(at: Timestamp, now: Timestamp): string {
  const day = formatRelativeDay(at, now)
  return day === 'today' ? formatTime(at) : `${day} ${formatTime(at)}`
}

function Plus() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" aria-hidden="true">
      <path
        d="M12 6v12M6 12h12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function Arrow() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
      <path
        d="M5 12h13M12 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
